import Table from '@/components/Table'
import { TableData } from '@/components/Table/type'
import { useConferenceHubQuery } from '@/queries/useConferenceHubQuery'
import { ConferenceHub } from '@/types/ConferenceHub'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react' 
import DefaultColumn from '@/components/Table/components/DefaultColumn'
import { useTable } from '@/hooks/useTable'
import { useState } from "react"
import { addDays, differenceInDays, format, subDays } from 'date-fns';
import DadosMestreHeader from '../components/DadosMestreHeader'
import DadosMestreHeaderActions from '../components/DadosMestreHeaderActions'
import { Eye } from 'lucide-react' 
import useDisclosure from '@/hooks/useDisclosure'
import AnalyzeDocument from './components/AnalyzeDocument'
import { formatDateToString } from '@/utils/date'
import { DatePicker } from '@/components/DatePicker'
import { TypographySmall } from '@/components/ui/typography'

export type ConferenceHubTable = TableData<ConferenceHub>

const columnHelper = createColumnHelper<ConferenceHub>()

const ProcessHubConferenceHub = () => {
    const {
        table,  
        selectedRows,  
        onClickMultiDelete, 
        setTable,
    } = useTable<ConferenceHub>('')
    const [initialDate, setInitialDate] = useState<Date>(new Date())
    const [finalDate, setFinalDate] = useState<Date>(new Date())
    const [selectData, setSelectData] = useState<ConferenceHub>()
 
    const {
        isOpen: isOpenAnalyzeDocument,
        onClose: onCloseAnalyzeDocument,
        onOpen: onOpenAnalyzeDocument,
    } = useDisclosure()


    const { data, isFetching, isPending, refetch } = useConferenceHubQuery({initialDate: Number(format(initialDate, 'yyyyMMdd'))  , finalDate:  Number(format(finalDate, 'yyyyMMdd'))  })
    
    const memoData = useMemo(() => data || [], [data])
    const memoColumns = useMemo(
        () => [
             columnHelper.display({
                id: 'Ações',
                cell: ({ row }) => { 
                    return (
                        (
                            <button onClick={() => {   
                                setSelectData(row.original)
                                onOpenAnalyzeDocument() 
                            }}>
                                <Eye size={14}/> 
                            </button>
                        )
                    )
                },
                size: 60,
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor(({ DD_PRODUTO_VALIDADO, DD_QUANTIDADE_VALIDADO, DD_IPI_VALIDADO, DD_CFOP_VALIDADO }) =>
                DD_PRODUTO_VALIDADO == 0 || DD_QUANTIDADE_VALIDADO == 0 || DD_IPI_VALIDADO == 0 || DD_CFOP_VALIDADO == 0 
                    ? 'NOK'
                    : 'OK',{
                id: 'Status',
                header: 'Status Linha',
                cell: props => (
                    <p className={`text-center w-full p-1 text-[#c9c9c9] ${props.getValue() == 'OK' ? 'bg-completed' : 'bg-timeout'} `}>{`${props.getValue()}`}</p>
                ),
                size: 120,
            }), 
            columnHelper.accessor('DS_EMPRESA_FORNECEDOR', {
                id: 'DS_EMPRESA_FORNECEDOR',
                header: 'Fornecedor',
                size: 150,
            }), 
            columnHelper.accessor('DS_EMPRESA_CLIENTE', {
                id: 'DS_EMPRESA_CLIENTE',
                header: 'Cliente',
                size: 140,
            }), 
            columnHelper.accessor('DD_DOCUMENTO', {
                id: 'DD_DOCUMENTO',
                header: 'Documento',
                size: 140,
            }), 
            columnHelper.accessor('DD_SERIE', {
                id: 'DD_SERIE',
                header: 'Serie',
                size: 80,
            }), 
            columnHelper.accessor(({ SK_TEMPO_DIGITACAO }) =>
                SK_TEMPO_DIGITACAO
                    ? formatDateToString(SK_TEMPO_DIGITACAO)
                    : '', {
                sortingFn: (rowA, rowB, _columnId) => {  
                    const statusA = Number(rowA.original[_columnId])
                    const statusB = Number(rowB.original[_columnId])
                    return statusA < statusB ? 1 : -1
                },
                id: 'SK_TEMPO_DIGITACAO',
                header: 'Dt. Digitação',
                size: 130,
            }), 
            columnHelper.accessor('NK_PRODUTO_SAIDA', {
                id: 'NK_PRODUTO_SAIDA',
                header: 'Cod. Produto',
                size: 140,
            }), 
            columnHelper.accessor(({ DD_PRODUTO_VALIDADO  }) =>
                DD_PRODUTO_VALIDADO == 0
                    ? 'NOK'
                    : 'OK',{
                id: 'DD_PRODUTO_VALIDADO',
                header: 'Status Produto', 
                cell: props => (
                    <p className={`text-center w-full p-1 text-[#c9c9c9] ${props.getValue() == 'OK' ? 'bg-completed' : 'bg-timeout'} `}>{`${props.getValue()}`}</p>
                ),
                size: 135,
            }),
            columnHelper.accessor(({ DD_QUANTIDADE_VALIDADO  }) =>
                DD_QUANTIDADE_VALIDADO == 0
                    ? 'NOK'
                    : 'OK',{
                id: 'DD_QUANTIDADE_VALIDADO',
                header: 'Status Qtd.', 
                cell: props => (    
                    <p className={`text-center w-full p-1 text-[#c9c9c9] ${props.getValue() == 'OK' ? 'bg-completed' : 'bg-timeout'} `}>{`${props.getValue()}`}</p>
                ),
                size: 135,
            }),
            columnHelper.accessor(({ DD_IPI_VALIDADO  }) =>
                DD_IPI_VALIDADO == 0
                    ? 'NOK'
                    : 'OK',{
                id: 'DD_IPI_VALIDADO',
                header: 'Status IPI', 
                cell: props => (
                    <p className={`text-center w-full p-1 text-[#c9c9c9] ${props.getValue() == 'OK' ? 'bg-completed' : 'bg-timeout'} `}>{`${props.getValue()}`}</p>
                ),
                size: 135,
            }),
            columnHelper.accessor(({ DD_CFOP_VALIDADO  }) =>
                DD_CFOP_VALIDADO == 0
                    ? 'NOK'
                    : 'OK',{
                id: 'DD_CFOP_VALIDADO',
                header: 'Status CFOP', 
                cell: props => (
                    <p className={`text-center w-full p-1 text-[#c9c9c9] ${props.getValue() == 'OK' ? 'bg-completed' : 'bg-timeout'} `}>{`${props.getValue()}`}</p>
                ),
                size: 135,
            }),
        ],
        []
    )

     const onChangeDate = ({DateInitial, DateFinal}) => {
            let differenceDays = 10
            if(DateInitial) {  
                if (differenceInDays(DateInitial, finalDate) < -differenceDays) {
                    setInitialDate(DateInitial)
                    setFinalDate(addDays(DateInitial,differenceDays)) 
                }
                if(DateInitial > finalDate){
                    setInitialDate(DateInitial)
                    setFinalDate(DateInitial)
                } else if (DateInitial <= finalDate) {
                    setInitialDate(DateInitial)
                }
            }
            if(DateFinal) { 
                console.log(differenceInDays(initialDate, DateFinal) )
               if (differenceInDays(initialDate,DateFinal) < -differenceDays) {
                   setInitialDate(subDays(DateFinal,differenceDays))
                   setFinalDate(DateFinal) 
               }
               if(DateFinal < initialDate){
                   setInitialDate(DateFinal)
                   setFinalDate(DateFinal)
               } else if (DateFinal >= initialDate) {
                    setFinalDate(DateFinal)
               }
           }
            
        }

    return (
        <div className="w-full h-full p-4 overflow-hidden">
            <Table<ConferenceHubTable>
                data={memoData}
                columns={memoColumns}
                getRowId={(row) => row.SK_CONFERENCIA_HUB.toString()}
                isLoading={isPending}
                isFetching={isFetching}
                getTableInstance={(table) => setTable(table)} 
                tableHeader={
                    <DadosMestreHeader
                        title={`Conferência Hub`}
                        onDelete={onClickMultiDelete}
                        selectedRows={
                            table
                                ?.getFilteredRowModel()
                                .flatRows.filter((row) => selectedRows[row.id])
                                .length
                        }
                    />
                }
                tableActions={
                    <DadosMestreHeaderActions 
                        onRefech={refetch} 
                        CustomButtons={(
                            <>
                                <TypographySmall>Data de Digitação entre: </TypographySmall>
                                <DatePicker date={initialDate} setDate={(date) => date && onChangeDate({DateInitial: date, DateFinal: null})} classNames={{day_selected: 'focus:bg-gray-400'}} />
                                <DatePicker date={finalDate} setDate={(date) => date && onChangeDate({DateInitial: null, DateFinal: date}) } classNames={{day_selected: 'focus:bg-gray-400'}} />
                            </>
                        )}
                    />
                } 
                defaultColumn={{
                    cell: ({ getValue }) => (
                        <DefaultColumn>{getValue() as string}</DefaultColumn>
                    ),
                }}
            /> 

            {selectData && 
                <AnalyzeDocument isOpen={isOpenAnalyzeDocument} onClose={onCloseAnalyzeDocument} data={selectData}/> 
            }
        </div>
    )
}

export default ProcessHubConferenceHub
